import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/currency';

const AccountTreeItem = ({ 
  account, 
  company,
  level = 0, 
  onEdit, 
  onDelete, 
  onToggleActive,
  onDragStart,
  onDragOver,
  onDrop 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = account?.children && account?.children?.length > 0;

  const getAccountTypeColor = (type) => {
    const colors = {
      'Asset': 'text-success',
      'Liability': 'text-warning',
      'Equity': 'text-accent',
      'Income': 'text-primary',
      'Expense': 'text-error'
    };
    return colors?.[type] || 'text-foreground';
  };

  const formatBalance = (balance) => {
    if (company?.currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: company.currency
      }).format(balance);
    }
    return formatCurrency(balance);
  };

  return (
    <div className="w-full">
      <div
        draggable
        onDragStart={(e) => onDragStart(e, account)}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, account)}
        className={`
          flex items-center gap-2 md:gap-3 lg:gap-4 p-3 md:p-4 rounded-md
          hover:bg-muted transition-smooth cursor-move
          ${!account?.isActive ? 'opacity-50' : ''}
          ${account?.hasActivity ? 'border-l-4 border-primary' : ''}
        `}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
      >
        {hasChildren && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-background rounded transition-smooth flex-shrink-0"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <Icon 
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
              size={16} 
            />
          </button>
        )}

        {!hasChildren && <div className="w-6" />}

        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center min-w-0">
          <div className="md:col-span-1 flex items-center gap-2">
            <span className="data-text text-xs md:text-sm text-muted-foreground whitespace-nowrap">
              {account?.code}
            </span>
          </div>

          <div className="md:col-span-4 flex items-center gap-2 min-w-0">
            <div className={`
              w-8 h-8 md:w-10 md:h-10 rounded-md flex items-center justify-center flex-shrink-0
              ${account?.type === 'Asset' ? 'bg-success/10' : ''}
              ${account?.type === 'Liability' ? 'bg-warning/10' : ''}
              ${account?.type === 'Equity' ? 'bg-accent/10' : ''}
              ${account?.type === 'Income' ? 'bg-primary/10' : ''}
              ${account?.type === 'Expense' ? 'bg-error/10' : ''}
            `}>
              <Icon 
                name={account?.icon} 
                size={16} 
                color={`var(--color-${account?.type === 'Asset' ? 'success' : account?.type === 'Liability' ? 'warning' : account?.type === 'Equity' ? 'accent' : account?.type === 'Income' ? 'primary' : 'error'})`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-body font-medium text-sm md:text-base text-foreground truncate">
                {account?.name}
              </p>
              {account?.description && (
                <p className="caption text-muted-foreground truncate hidden lg:block">
                  {account?.description}
                </p>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <span className={`caption font-medium ${getAccountTypeColor(account?.type)}`}>
              {account?.type}
            </span>
          </div>

          <div className="md:col-span-2 text-right">
            <span className="data-text text-sm md:text-base font-medium text-foreground whitespace-nowrap">
              {formatBalance(account?.balance)}
            </span>
          </div>

          <div className="md:col-span-3 flex items-center justify-end gap-1 md:gap-2">
            {account?.hasActivity && (
              <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-md">
                <Icon name="Activity" size={12} color="var(--color-primary)" />
                <span className="caption text-primary hidden lg:inline">Active</span>
              </div>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(account)}
              iconName="Edit2"
              iconSize={16}
              className="flex-shrink-0"
            />

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleActive(account)}
              iconName={account?.isActive ? 'Eye' : 'EyeOff'}
              iconSize={16}
              className="flex-shrink-0"
            />

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(account)}
              iconName="Trash2"
              iconSize={16}
              className="text-error hover:text-error flex-shrink-0"
            />
          </div>
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {account?.children?.map((child) => (
            <AccountTreeItem
              key={child?.id}
              account={child}
              company={company}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountTreeItem;